import { motion } from 'framer-motion'
import { HiCheck } from 'react-icons/hi'
import { MdPeople, MdAccessTime } from 'react-icons/md'
import { FaWhatsapp } from 'react-icons/fa'
import Button from '../ui/Button'
import { formatPrice } from '../../utils/formatPrice'
import { useSite } from '../../context/SiteContext'

/**
 * PackageCard — supports both flat props and legacy `pkg` object.
 *
 * Flat props (preferred):
 *   title, price, currency, maxGuests, durationHours,
 *   features (string[]), badge, isPopular, accentColor, index
 *
 * Legacy: pass `pkg` object — fields are mapped automatically.
 */
export default function PackageCard({
  // Flat props
  title:         titleProp,
  price:         priceProp,
  currency:      currencyProp,
  maxGuests:     maxGuestsProp,
  durationHours: durationHoursProp,
  features:      featuresProp,
  badge:         badgeProp,
  isPopular:     isPopularProp,
  accentColor:   accentColorProp,
  // Legacy pkg object
  pkg,
  index = 0,
}) {
  // Merge: flat props take priority over pkg object values
  const title         = titleProp         ?? pkg?.title
  const price         = priceProp         ?? pkg?.price
  const currency      = currencyProp      ?? pkg?.currency      ?? 'EGP'
  const maxGuests     = maxGuestsProp     ?? pkg?.max_guests
  const durationHours = durationHoursProp ?? pkg?.duration_hours
  const features      = featuresProp      ?? pkg?.features      ?? []
  const badge         = badgeProp         ?? pkg?.badge
  const isPopular     = isPopularProp     ?? pkg?.is_featured   ?? false
  const accentColor   = accentColorProp   ?? pkg?.color_accent  ?? '#FF6BB5'
  const subtitle      = pkg?.subtitle

  const { siteOptions } = useSite()
  const waNumber = siteOptions?.contact?.whatsapp || '201000000000'
  const waMessage = encodeURIComponent(`Hi! I'm interested in the ${title} package. Can you tell me more?`)

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ delay: index * 0.15, duration: 0.6, ease: 'easeOut' }}
      className={[
        'relative rounded-3xl overflow-hidden flex flex-col',
        'transition-all duration-300',
        isPopular
          ? 'scale-[1.03] z-10 shadow-glow-primary ring-4 ring-primary'
          : 'shadow-card hover:shadow-glow-primary hover:-translate-y-1',
      ].join(' ')}
    >
      {/* Most popular banner */}
      {isPopular && (
        <div className="bg-gradient-cta py-2.5 text-center text-white text-xs font-body font-bold tracking-widest uppercase">
          ⭐ Most Popular Choice
        </div>
      )}

      {/* ── Gradient header ───────────────────────────────────────────────── */}
      <div
        className="p-8 text-white relative overflow-hidden"
        style={{
          background: isPopular
            ? 'linear-gradient(135deg, #FF6BB5 0%, #7C3AFF 100%)'
            : `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}bb 100%)`,
        }}
      >
        {/* Background orb */}
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />

        {/* Badge */}
        {badge && (
          <span className="inline-block mb-3 px-3 py-1 bg-white/20 text-white text-xs font-body font-bold rounded-full border border-white/30">
            {badge}
          </span>
        )}

        {/* Title */}
        <h3 className="font-display text-3xl text-white mb-1">{title}</h3>
        {subtitle && <p className="text-white/80 text-sm font-body mb-3">{subtitle}</p>}

        {/* Price */}
        <div className="font-display text-5xl text-white mt-4 mb-1 leading-none">
          {formatPrice(price, currency)}
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-4 mt-3">
          {maxGuests && (
            <span className="flex items-center gap-1.5 text-white/80 text-sm font-body">
              <MdPeople size={16} /> Up to {maxGuests} guests
            </span>
          )}
          {durationHours && (
            <span className="flex items-center gap-1.5 text-white/80 text-sm font-body">
              <MdAccessTime size={16} /> {durationHours} hours
            </span>
          )}
        </div>
      </div>

      {/* ── Features list ─────────────────────────────────────────────────── */}
      <div className="bg-surface p-8 flex-1 flex flex-col">
        <ul className="space-y-3 flex-1 mb-8">
          {features.map((feature, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-ink-muted font-body">
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <HiCheck size={12} style={{ color: accentColor }} />
              </span>
              {feature}
            </li>
          ))}
        </ul>

        {/* ── Action buttons ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          <Button
            to="/contact"
            state={{ package: title }}
            variant={isPopular ? 'primary' : 'secondary'}
            className="w-full"
          >
            Book This Package 🎉
          </Button>

          <Button
            href={`https://wa.me/${waNumber}?text=${waMessage}`}
            variant="whatsapp"
            size="sm"
            className="w-full"
            icon={<FaWhatsapp size={16} />}
          >
            Ask on WhatsApp
          </Button>
        </div>
      </div>
    </motion.div>
  )
}
